import { z } from 'zod';

/**
 * @aiPurpose discriminator separating an ordinary `STANDARD` request
 *   (boxes allowed, item weight/volume required) from a `CONTAINER`
 *   request (boxes forbidden, `transportTypeId` required, item
 *   weight/volume optional) and a `SIMPLE` request (#1303 — a
 *   normal-request-like kind that additionally forbids insurance,
 *   boxes, `transportTypeId`, and the container `deliveryAddress` /
 *   `pickupAddress`).
 *   Mirrors the Prisma `RequestKind` enum in
 *   `apps/backend/prisma/schema/request-kind.prisma`; the parity is
 *   guarded by `scripts/check-enum-sync.sh`.
 * @aiInvariant default for the create input is `STANDARD` — historical
 *   semantics — so callers that omit `kind` produce a STANDARD-shaped
 *   row.
 */
export const RequestKindSchema = z.enum(['STANDARD', 'CONTAINER', 'SIMPLE']);
export type RequestKind = z.infer<typeof RequestKindSchema>;

export const isContainerKind = (k: RequestKind): k is 'CONTAINER' => k === 'CONTAINER';
export const isSimpleKind = (k: RequestKind): k is 'SIMPLE' => k === 'SIMPLE';

/**
 * @aiPurpose name the set of request kinds whose write paths forbid
 *   `insuranceRequested = true` on the wire. SIMPLE forbids insurance per
 *   #1052/#1303; #1453 extends the same write-side rejection to STANDARD
 *   ("Сложная заявка") after its insurance UI was removed (#1448).
 * @aiInvariant CONTAINER is intentionally excluded — it still PERMITS
 *   insurance on the wire. Defined as an explicit positive (SIMPLE +
 *   STANDARD) rather than `!isContainerKind` so the intent survives any
 *   future kind being added.
 */
export const kindForbidsInsurance = (k: RequestKind): boolean =>
  isSimpleKind(k) || k === 'STANDARD';
